import React, { useState, useRef, useEffect } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';

const AudioRecorder = ({ onTranscriptionComplete, selectedModel, onAudioProcessed, transcribeAudioMock }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [processed, setProcessed] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startRecording = async () => {
    setError('');
    setProcessed(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
        if (onAudioProcessed) {
          onAudioProcessed(audioBlob);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Error accessing microphone. Please ensure your browser has permission to use the microphone.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    }
  };

  const processRecording = async () => {
    if (!audioBlob) return;
    
    setIsProcessing(true);
    setError('');
    
    try {
      // USE YOUR PROVIDED METHOD
      const response = await transcribeAudioMock(audioBlob);
      
      if (response && response.transcription) {
        onTranscriptionComplete(response.transcription);
        setProcessed(true);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error processing audio:', err);
      setError(`Failed to process audio: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        if (mediaRecorderRef.current.stream) {
          mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
      }
    };
  }, []);

  return (
    <div className="audio-recorder">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      
      <div className="d-flex justify-content-center mb-3">
        {!audioBlob ? (
          isRecording ? (
            <div className="text-center">
              <div className="mic-animation mb-3">
                <i className="bi bi-mic-fill text-danger fs-1"></i>
                <div className="pulse-ring"></div>
              </div>
              <p>Recording in progress...</p>
              <Button 
                variant="danger" 
                className="px-4"
                onClick={stopRecording}
              >
                Stop Recording
              </Button>
            </div>
          ) : (
            <Button 
              variant="primary" 
              className="px-4"
              onClick={startRecording}
            >
              <i className="bi bi-mic me-2"></i> Start Speaking
            </Button>
          )
        ) : (
          <div className="d-flex gap-2">
            <Button 
              variant="secondary" 
              onClick={() => {
                setAudioBlob(null);
                setError('');
                setProcessed(false);
              }}
            >
              Record Again
            </Button>
            <Button 
              variant="primary" 
              onClick={processRecording}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processing...
                </>
              ) : 'Process Recording'}
            </Button>
          </div>
        )}
      </div>
      
      {audioBlob && (
        <div className="mb-3">
          <audio src={URL.createObjectURL(audioBlob)} controls className="w-100" />
          {!processed && (
            <div className="mt-2 text-muted text-center">
              <small>
                <i className="bi bi-info-circle me-1"></i>
                Click "Process Recording" to transcribe your audio using Whisper API
              </small>
            </div>
          )}
        </div>
      )}
      
      <div className="d-none">{processed ? 'processed' : 'unprocessed'}</div>
    </div>
  );
};

export default AudioRecorder;
